// Angular imports
import { Injectable } from '@angular/core';

// Import sanitize html

// Import services
import { LoggedInHttpService } from './data/logged-in-http.service';
import { ApplicationContextService } from '../services/application-context.service';

// Import observable
import { map, Observable, Subscriber } from 'rxjs';

// Import types
import { AfasProject, AfasProjectResponse, Project } from '../types/project';
import { CalculationApi, CalculationHeaderApi, CalculationTailApi, CalculationLineApi, CalculationMetaApi } from '../types/calculation';
import { VersionHistory } from '../types/version-history';
import { CostsEntity } from '../types/budget-check';

export enum ExportType {
    quotation = 'extern-offerte',
    hours = 'extern-uren',
    budget = 'intern-open-begroting',
    smeulders = 'smeulders-offerte',
    projects = 'projects-offerte',
    net = 'netto',
    gross = 'bruto',
    quantities = 'hoeveelheden',
    pga = 'pga-offerte',
    mmw = 'mmw-specification'
};

@Injectable({
    providedIn: 'root'
})
export class DataService {
    // New approach, with Redux state management
    // To avoid redesiging the entire application, here is just a bit first
    public loadRawCalculation(projectId: string, calculationId: string, version: number = null): Observable<CalculationApi> {
        return this.http._get<CalculationApi>(`/project/${projectId}/calculation/${calculationId}` + (version ? `/version/${version}` : ''));
    }

    public createCalculation(project: Project, type: string) {
        return this.http._post<CalculationApi>(`/project/${project.id}/calculation/type/${type}`, {
            description: project.description,
            client: project.client,
            address: { ...project.address },
        });
    }

    public copyCalculation$(projectId: string, calculationId: string) {
        return this.http._post<CalculationApi>(`/project/${projectId}/calculation/${calculationId}/copy-calculation`, {});
    }

    public copyCalculationVersion$(projectId: string, calculationId: string, versionId: number) {
        return this.http._post<CalculationMetaApi>(`/project/${projectId}/calculation/${calculationId}/copy-version/${versionId}`, {});
    }

    public updateLine$(projectId: string, calculationId: string, line: CalculationLineApi) {
        return this.http._put<CalculationLineApi>(`/project/${projectId}/calculation/${calculationId}/part/${line.code.replace('.', '')}/line/${line.id}`, line);
    }

    public createLine$(projectId: string, calculationId: string, line: CalculationLineApi) {
        return this.http._post<CalculationLineApi>(`/project/${projectId}/calculation/${calculationId}/part/${line.code.replace('.', '')}/line`, line);
    }

    public deleteLine$(projectId: string, calculationId: string, line: CalculationLineApi) {
        return this.http._delete(`/project/${projectId}/calculation/${calculationId}/part/${line.code.replace('.', '')}/line/${line.id}`);
    }

    public undeleteLine$(projectId: string, calculationId: string, line: CalculationLineApi) {
        return this.http._post<CalculationLineApi>(`/project/${projectId}/calculation/${calculationId}/part/${line.code.replace('.', '')}/line/${line.id}/undelete`, {});
    }

    public bulkAddLines$(projectId: string, calculationId: string, lines: CalculationLineApi[]) {
        return this.http._post<CalculationLineApi[]>(`/project/${projectId}/calculation/${calculationId}/lines`, lines);
    }

    public bulkUpdateLines$(projectId: string, calculationId: string, lines: CalculationLineApi[]) {
        return this.http._put<CalculationLineApi[]>(`/project/${projectId}/calculation/${calculationId}/lines`, lines);
    }

    public bulkDeleteLines$(projectId: string, calculationId: string, lines: CalculationLineApi[]) {
        return this.http._post<CalculationLineApi[]>(`/project/${projectId}/calculation/${calculationId}/bulk-delete-lines`, lines);
    }

    public bulkUndeleteLines$(projectId: string, calculationId: string, lines: CalculationLineApi[]) {
        return this.http._post<CalculationLineApi[]>(`/project/${projectId}/calculation/${calculationId}/bulk-undelete-lines`, lines);
    }

    public updateCalculationMeta$(projectId: string, calculationId: string, meta: CalculationMetaApi) {
        return this.http._put<CalculationMetaApi>(`/project/${projectId}/calculation/${calculationId}/meta`, meta)
    }

    public updateHeaderLine$(projectId: string, calculationId: string, line: CalculationHeaderApi) {
        return this.http._put(`/project/${projectId}/calculation/${calculationId}/header/${line.id}`, line);
    }

    public createTailLine$(projectId: string, calculationId: string, line: CalculationTailApi) {
        return this.http._post<CalculationTailApi>(`/project/${projectId}/calculation/${calculationId}/tail`, line);
    }

    public bulkAddTailLines$(projectId: string, calculationId: string, lines: CalculationTailApi[]) {
        return this.http._post<CalculationTailApi[]>(`/project/${projectId}/calculation/${calculationId}/tail/lines`, lines);
    }

    public updateTailLine$(projectId: string, calculationId: string, line: CalculationTailApi) {
        return this.http._put(`/project/${projectId}/calculation/${calculationId}/tail/${line.id}`, line);
    }

    public bulkUpdateTailLines$(projectId: string, calculationId: string, lines: CalculationTailApi[]) {
        return this.http._put<CalculationTailApi[]>(`/project/${projectId}/calculation/${calculationId}/tail/lines`, lines);
    }

    public deleteTailLine$(projectId: string, calculationId: string, line: CalculationTailApi) {
        return this.http._delete(`/project/${projectId}/calculation/${calculationId}/tail/${line.id}`);
    }

    public undeleteTailLine$(projectId: string, calculationId: string, line: CalculationTailApi) {
        return this.http._post(`/project/${projectId}/calculation/${calculationId}/tail/${line.id}/undelete`, {});
    }

    public bulkUndeleteTailLine$(projectId: string, calculationId: string, lines: CalculationTailApi[]) {
        return this.http._post(`/project/${projectId}/calculation/${calculationId}/tail/bulk-undelete-lines`, lines);
    }

    public copyVersion$(projectId: string, calculationId: string, versionId: string) {
        return this.http._post(`/project/${projectId}/calculation/${calculationId}/copy-version/${versionId}`, {});
    }

    public createVariationsFromCalculation$(projectId: string, calculationId: string) {
        return this.http._post<CalculationApi>(`/project/${projectId}/calculation/${calculationId}/create-variations`, {});
    }

    public unlockCalculation$(projectId: string, calculationId: string) {
        return this.http._post(`/project/${projectId}/calculation/${calculationId}/lock`, {});
    }

    public deleteHeaderLine$(projectId: string, calculationId: string, headerLine: CalculationHeaderApi) {
        return this.http._delete<CalculationHeaderApi>(`/project/${projectId}/calculation/${calculationId}/header/${headerLine.id}`);
    }

    public createHeaderLine$(projectId: string, calculationId: string, headerLine: CalculationHeaderApi) {
        return this.http._post(`/project/${projectId}/calculation/${calculationId}/header`, headerLine);
    }

    public addHourlyRate$(projectId: string, calculationId: string, hourlyRate: CalculationHeaderApi) {
        return this.http._post<CalculationHeaderApi>(`/project/${projectId}/calculation/${calculationId}/header`, hourlyRate);
    }

    public getAfasProjects(): Observable<AfasProject[]> {
        return this.http._get<AfasProjectResponse>(`/projects`).pipe(
            map((response: AfasProjectResponse) => response.rows ?? [])
        );
    }

    public getCalculations(): Observable<CalculationApi[]> {
        return this.http._get<CalculationApi[]>(`/calculations`);
    }

    // =============================================================================
    public onError: Observable<any>;
    private onErrorSubscriptions: Subscriber<any>[] = [];
    private latestRequest?: number;

    constructor(
        private http: LoggedInHttpService, private context: ApplicationContextService) {
        // console.log('DataService instance created');
        this.onError = new Observable((subscriber) => {
            this.onErrorSubscriptions.push(subscriber);
            return () => {
                this.onErrorSubscriptions.splice(this.onErrorSubscriptions.indexOf(subscriber), 1);
            };
        });
    }

    private emitError(err: any, resend?: () => Promise<any>) {
        if (err.status === 444) {
            err.resend = resend
        }

        // Trigger error
        this.onErrorSubscriptions.forEach(sub => sub.next(err));

        // Report error
        this.reportError(err);
    }

    // public async getProjects(): Promise<Project[]> {
    //     try {
    //         const [calcs, { rows }] = await Promise.all([
    //             this.http.get('/calculations') as Promise<CalculationApi[]>,
    //             this.http.get('/projects')
    //         ]);

    //         // Get all running projects
    //         const runningProjects = (rows as any[]).map(project => {
    //             const projectCalcs = calcs
    //                 .filter(calc => calc['meta']['projectId'] == project['Projectnummer_'])

    //             return this.mapProject(project, projectCalcs);
    //         });

    //         // Override invalid version dates
    //         runningProjects.forEach(project => {
    //             if (!project.calculations) return;

    //             project.calculations
    //                 .filter(c => isNaN(new Date(c.meta.versionDate).valueOf()))
    //                 .forEach(c => {
    //                     // Re-assign version date
    //                     c.meta.versionDate = project.created.toISOString();
    //                 });
    //         });

    //         // Get all calculations for which there is no project (closed projects)
    //         const projectlessCalculations = calcs.filter(calc => !(rows as any[]).find(row => calc['meta']['projectId'] === row['Projectnummer_']));

    //         // Map all calculations without a project to closed projects
    //         const closedProjects = projectlessCalculations.reduce((projects: Project[], calc) => {
    //             // Attempt to find a matching project
    //             const project = projects.find(p => p.id === calc['meta']['projectId']) ||
    //                 projects[projects.push(this.mapProject({
    //                     Projectnummer_: calc['meta']['projectId'],
    //                     Omschrijving: calc['meta']['description'],
    //                     Naam: calc['meta']['client'] ?? '',
    //                     Land: calc['meta']['address']?.['country'] ?? '',
    //                     Woonplaats: calc['meta']['address']?.['city'] ?? '',
    //                     Postcode: calc['meta']['address']?.['postalCode'] ?? '',
    //                     Straat: calc['meta']['address']?.['address'] ? calc['meta']['address']['address'].split(' ')[0] : undefined,
    //                     'Toev._voor_straat': undefined,
    //                     Huisnummer: undefined,
    //                     'Toev._aan_huisnr.': undefined,
    //                     Datum_toegevoegd: new Date(calc['meta']['versionDate']).valueOf(),
    //                     Afgemeld: true
    //                 }, [])) - 1];

    //             // Add calculation to project
    //             project.calculations!.push(calc)

    //             // Return projects
    //             return projects;
    //         }, []);

    //         const totalCalcs = runningProjects.concat(closedProjects).reduce((calcs, project) => {
    //             calcs.push(...(project.calculations ?? []));
    //             return calcs;
    //         }, [] as CalculationApi[]);

    //         // Gather all running and closed projects
    //         const projects = runningProjects.concat(closedProjects);

    //         // Filter out invalid calculations
    //         projects.forEach(p => {
    //             p.calculations = p.calculations?.filter(c => !isNaN(new Date(c.meta.versionDate)?.valueOf()));
    //         });

    //         // Return projects
    //         return projects;
    //     } catch (err) {
    //         this.emitError(err);
    //     }
    // }

    public async exportCalculationAsPdf(projectId: string, calculationId: string, version: number, type: ExportType, includeTerms: boolean = true): Promise<Blob> {
        try {
            return this.http.get(`/project/${projectId}/calculation/${calculationId}/version/${version}/export/pdf?type=${type}&include-terms=${includeTerms}`, {
                headers: {
                    'Accept': 'application/pdf'
                },
                responseType: 'blob'
            });
        } catch (err) {
            this.emitError(err);
        }
    }

    public async exportCalculationAsCuf(projectId: string, calculationId: string, version: number): Promise<any> {
        try {
            return this.http.get(`/project/${projectId}/calculation/${calculationId}/version/${version}/export/cuf`, {
                headers: {
                    'Accept': 'application/xml',
                },
                responseType: 'test'
            });
        } catch (err) {
            this.emitError(err);
        }
    }

    public async reportError(error: Error): Promise<void> {
        this.http.post('/error-report', { error });
    }

    public async getVersionHistory(): Promise<VersionHistory[]> {
        const result = await this.http.get(`/trello/version-history`);

        return result;
    }

    /**
     * Get the budget check, containing all calculation items plus the registered invoices for a project
     * @param projectId The project that is used to get the invoices from AFAS
     * @param calculationId The calculation used for the budget amounts (specific version)
     * @returns Only the invoices are returned atm
     */
    public async getBudgetCheck(projectId: string, calculationId: string): Promise<CostsEntity[]> {
        try {
            const { costs } = await this.http.post<any>(`/budget-check/from-calculation/${projectId}/calculation/${calculationId}`, undefined);

            return Object.values(costs);
        } catch (err) {
            // Construct request error
            this.emitError(err);
        }
    }
}
